import { baseApi } from "shared/api";
import {
  Employee,
  EmployeeHistory,
  OmittedEmployee,
  Status,
} from "../model/employee";
import { casdoorApi } from "entitites/casdoor/api/casdoorApi";

export const employeesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllEmployees: builder.query<Array<Employee>, string>({
      query: (cursor) => ({
        url: `/employees/all/${cursor}`,
        method: "GET",
      }),
      serializeQueryArgs: ({ endpointName }) => {
        return endpointName;
      },
      merge: (currentCache, newItems) => {
        currentCache.push(...newItems);
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg;
      },
      providesTags: [{ type: "Employees", id: "LIST" }],
    }),
    getAllEmployeesBySearchString: builder.query<Array<Employee>, string>({
      query: (searchString) => ({
        url: `/employees/all/${searchString}`,
        method: "GET",
      }),
      providesTags: [{ type: "Employees", id: "SEARCH" }],
    }),
    getAllStatuses: builder.query<Status[], void>({
      query: () => ({
        url: `/employees/statuses`,
        method: "GET",
      }),
      keepUnusedDataFor: 16000,
      providesTags: ["Statuses"],
    }),
    getEmployeeHistory: builder.query<EmployeeHistory[], string>({
      query: (id) => ({
        url: `/employees/history/${id}`,
        method: "GET",
      }),
      providesTags: (res, _, arg) => [
        { type: "Employees", id: `History ${arg}` },
      ],
    }),
    getEmployeeById: builder.query<Employee, string>({
      query: (id) => ({
        url: `/employees/${id}`,
        method: "GET",
      }),
      providesTags: (res, _, arg) => [{ type: "Employees", id: arg }],
    }),
    deleteEmployeeById: builder.mutation<Employee, string>({
      query: (id) => ({
        url: `/employees/${id}`,
        method: "DELETE",
      }),
      onQueryStarted(id, { dispatch, queryFulfilled, getState }) {
        queryFulfilled.then((data) =>
          dispatch(
            employeesApi.util.updateQueryData(
              "getAllEmployees",
              `${getState().api.queries.getAllEmployees?.originalArgs}`,
              (draft) => {
                return draft.filter((item) => item.id !== id);
              }
            )
          )
        );
      },
    }),
    updateEmployeeById: builder.mutation<
      Employee,
      { id: string; body: OmittedEmployee }
    >({
      query: ({ id, body }) => ({
        url: `/employees/${id}`,
        method: "PUT",
        body: { ...body },
      }),

      onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
        queryFulfilled.then((data) => {
          getState().api.queries[`getUserRoles("${arg.id}")`] &&
            getState().api.queries[`getUserRoles("${arg.id}")`]?.data &&
            dispatch(
              casdoorApi.endpoints.updateUserRoles.initiate({
                data: getState().api.queries[`getUserRoles("${arg.id}")`]
                  ?.data as any,
                userId: arg.id,
              })
            );
          dispatch(
            employeesApi.util.updateQueryData(
              "getEmployeeById",
              `${arg.id}`,
              (draft) => {
                return data.data;
              }
            )
          );
          dispatch(
            employeesApi.util.updateQueryData(
              "getAllEmployees",
              `${getState().api.queries.getAllEmployees?.originalArgs}`,
              (draft) => {
                return [
                  data.data,
                  ...draft.filter((item) => item.id !== data.data.id),
                ];
              }
            )
          );
        });
      },
      invalidatesTags: (res, _, arg) => [
        { type: "Employees", id: `History ${arg.id}` },
        { type: "Employees", id: "SEARCH" },
      ],
    }),
    getAllPositions: builder.query<Status[], void>({
      query: () => ({
        url: `/employees/positions`,
        method: "GET",
      }),
      keepUnusedDataFor: 16000,
      providesTags: ["Positions"],
    }),
    createEmployee: builder.mutation<Employee, OmittedEmployee>({
      query: (employee) => ({
        url: "employees",
        method: "POST",
        body: { ...employee },
      }),
      onQueryStarted(_, { dispatch, queryFulfilled, getState }) {
        queryFulfilled
          .then((data) =>
            dispatch(
              employeesApi.util.updateQueryData(
                "getAllEmployees",
                `${getState().api.queries.getAllEmployees?.originalArgs}`,
                (draft) => {
                  return [data.data, ...draft];
                }
              )
            )
          )
          .catch((er) => console.log(er));
      },
    }),
  }),
});

export const {
  useCreateEmployeeMutation,
  useGetAllEmployeesQuery,
  useGetAllStatusesQuery,
  useGetAllPositionsQuery,
  useGetEmployeeByIdQuery,
  useUpdateEmployeeByIdMutation,
  useLazyGetAllEmployeesBySearchStringQuery,
  useGetEmployeeHistoryQuery,
  useDeleteEmployeeByIdMutation,
} = employeesApi;
